import {
	ChangeDetectionStrategy,
	Component,
	effect,
	inject,
	OnDestroy,
	OnInit,
	signal,
	untracked,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { SnigelService } from '@uc/web/core';
import { LoadingAnimationService } from '@uc/shared/ui';
import { SeoMetadataService } from '@uc/utilities';
import { GuideCategoryUrl } from '@uc/web/shared/data-models';
import {
	ArticleContainerComponent,
	EngageCardCtaComponent,
	StaticCardComponent,
} from '@web/shared/ui';
import { RecommendedUnisCarouselComponent } from '@uc/web/shared/feature/feature-recommended-universities';
import { StickyLeadBarRendererComponent } from '@web/shared/feature/feature-sticky-lead-bar';
import { getSingleGuide } from '../../data-access/+state/guides.selectors';
import { fetchSingleGuide } from '../../data-access/+state/guides.actions';
import { GuidesService } from '../../services/guides.service';
import { RANKINGS_MAPPING } from '../../static/guides.properties';

@Component({
	selector: 'uc-guides-article',
	templateUrl: './guides-article.component.html',
	imports: [
		RouterLink,
		EngageCardCtaComponent,
		ArticleContainerComponent,
		StickyLeadBarRendererComponent,
		RecommendedUnisCarouselComponent,
		StaticCardComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidesArticleComponent implements OnInit, OnDestroy {
	private _store = inject(Store);
	private _route = inject(ActivatedRoute);
	private _router = inject(Router);
	private _loadingSrv = inject(LoadingAnimationService);
	private _seoSrv = inject(SeoMetadataService);
	private _guidesSrv = inject(GuidesService);
	private _snigelSrv = inject(SnigelService);

	singleGuide = this._store.selectSignal(getSingleGuide);
	guide = signal(null);
	loadingGuide = signal(true);
	loadingHtml = signal<string>(this._loadingSrv.createPara(2, 3));
	guideCategoryLongName = signal<string>('');
	guideCategory = signal<GuideCategoryUrl>('' as GuideCategoryUrl);
	guideName = signal<string>('');
	guideCategoryUrl = signal<string>('');
	url = signal<string>('');
	subject = signal<string>('');
	courseSearchSlug = signal<string | undefined>('');
	rankingsSlug = signal<string | undefined>('');
	private _routerSub!: Subscription;

	guideEffect = effect(() => {
		const singleGuide = this.singleGuide();
		if (!singleGuide) return;

		const guide = { ...singleGuide };

		untracked(() => {
			guide.content = this._snigelSrv.insertAdsIntoContent(guide.content);
			this.guide.set(guide);
			this._seoSrv.updateTitle(guide.seotitle);
			this._seoSrv.updateDescription(guide.seodesc);
			this.url.set(`https://universitycompare.com${this._router.url}`);

			if (guide.catid === 1) {
				const subject = RANKINGS_MAPPING.find(
					(subject) =>
						subject.guideName.toLowerCase() === guide.title.toLowerCase(),
				);
				this.courseSearchSlug.set(subject?.courseSearchSlug);
				this.rankingsSlug.set(subject?.rankingsSlug);
				this.subject.set(guide.title.toLowerCase().replace(' degree', ''));
			}

			//create twitter card
			const og = {
				title: guide.title,
				description: guide.seodesc,
				type: 'website',
				url: this.url(),
				image: guide.imgname,
			};

			this._seoSrv.createOgTags('property', og, 'og');
			this._seoSrv.createOgTags('name', this._seoSrv.twitterConfig, 'twitter');

			this.guideCategoryLongName.set(this._guidesSrv.getGuideCategory(guide.catid));
			this.loadingGuide.set(false);
		});
	});

	getAdds = effect(() => {
		if (this.guide()) {
			this._snigelSrv.getAds([
				'incontent_1_dskt',
				'incontent_2_mob',
				'incontent_3_dskt',
				'incontent_3_mob',
				'incontent_5_dskt',
				'incontent_5_mob',
				'incontent_6_dskt',
				'incontent_6_mob',
			]);
		}
	});

	ngOnInit(): void {
		this._routerSub = this._route.params.subscribe((params) => {
			this.guideCategory.set(params['category']);
			this.guideName.set(params['guideName']);
			this.guideCategoryUrl.set(`/guides/${this.guideCategory()}`);
			const categoryId = this._guidesSrv.getCategoryId(this.guideCategory());
			this._store.dispatch(
				fetchSingleGuide({
					guideName: this.guideName(),
					categoryId: categoryId,
				}),
			);
		});
	}

	ngOnDestroy(): void {
		this._routerSub.unsubscribe();
	}
}
